import React, { useState, useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import { hexToBytes } from '@noble/hashes/utils';
import { bech32 } from '@scure/base';
import '../App.css';
import ReservedNamesIcon from '../Images/ReservedNames.svg';
import PromoCodesIcon from '../Images/PromoCodes.svg';
import DefaultAvatar from '../Images/default avatar.svg';
import ChangeName from '../Images/ChangeName.svg';
import MonthlyRevenuesGraph from './MonthlyRevenuesGraph';
import refresh from '../Images/refreshButton.svg';
import expiredButton from '../Images/expiredButton.svg';
import LegendsButton from '../Images/LegendsButton.svg';

const PremiumSubs = () => {
    const history = useHistory();
    const [subscriptionData, setSubscriptionData] = useState(null);
    const [tableData, setTableData] = useState([]);
    const [monthlyRevenueAverage, setMonthlyRevenueAverage] = useState(0);
    const [previousMonthRevenue, setPreviousMonthRevenue] = useState(0);
    const [currentMonthRevenue, setCurrentMonthRevenue] = useState(0);
    const [projectedMonthRevenue, setProjectedMonthRevenue] = useState(0);
    const [legendMonthlyAverage, setLegendMonthlyAverage] = useState(0);
    const [previousMonthLegendRevenue, setPreviousMonthLegendRevenue] = useState(0);
    const [currentMonthLegendRevenue, setCurrentMonthLegendRevenue] = useState(0);
    const [legendRevenueProjection, setLegendRevenueProjection] = useState(0);
    const [premiumMonthlyAverage, setPremiumMonthlyAverage] = useState(0);
    const [previousMonthPremiumRevenue, setPreviousMonthPremiumRevenue] = useState(0);
    const [currentMonthPremiumRevenue, setCurrentMonthPremiumRevenue] = useState(0);
    const [premiumRevenueProjection, setPremiumRevenueProjection] = useState(0);
    const [avatars, setAvatars] = useState({});
    const [avatarsLoading, setAvatarsLoading] = useState(true);
    const [loading, setLoading] = useState(true);

    const navigateToReservedNames = () => {
        history.push('/premium/reserved_names');
    };

    const navigateToPromoCodes = () => {
        history.push('/premium/promo_codes');
    };

    const navigateToChangeName = () => {
        history.push('/premium/change_name');
    };

    const navigateToExpiredSubs = () => {
        history.push('/premium/expired_subs');
    };

    const navigatetoLegends = () => {
        history.push('/premium/legends');
    }

    const calculateSubAnnualValue = (activeSubs, activePros) => (activeSubs * 5.5 * 12) + (activePros * 750);

    const calculateSubRevenueProjection = (subAnnualValue, newSubs24h) =>
        subAnnualValue + newSubs24h * 365 * 12 * 5.5;

    const npubEncode = (hex) => {
        const words = bech32.toWords(hexToBytes(hex));
        return bech32.encode('npub', words);
    };

    const fetchAvatars = async (pubKeys) => {
        const payload = JSON.stringify(["user_infos", { "pubkeys": pubKeys }]);
        try {
            const response = await fetch("https://dev.primal.net/api", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                },
                body: payload,
            });
    
            if (response.ok) {
                const data = await response.json();
                const avatarsData = {};
                if (data && Array.isArray(data)) {
                    data.forEach(user => {
                        try {
                            const userData = JSON.parse(user.content); // Parse the 'content' field
                            avatarsData[user.pubkey] = userData.picture || DefaultAvatar; // Extract 'picture' or use default
                        } catch (err) {
                            console.error("Error parsing user content:", err);
                        }
                    });
                }
                setAvatars(avatarsData);
            } else {
                console.error("Error fetching avatars:", response.statusText);
            }
        } catch (error) {
            console.error("Error fetching avatars:", error);
        }
        setAvatarsLoading(false); // Set to false when avatars are fully loaded
    };

    const fetchSubscriptionData = async () => {
            try {
                const e = await window.nostr.signEvent({
                    created_at: Math.floor(new Date().getTime() / 1000),
                    kind: 10000222,
                    tags: [],
                    content: 'i want in',
                });
    
                const response = await fetch('https://admin.primal.net/api2', {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/json' },
                    body: JSON.stringify({
                        event_from_user: e,
                        request: ['subscriptions', { limit: 10000, offset: 0 }],
                    }),
                });
    
                const data = await response.json();
                    if (data && !data.error) {
                        setSubscriptionData(data);
                        setTableData(data.subscriptions);

                        const pubKeys = [...new Set(data.subscriptions.map(sub => sub.receiver_pubkey))];
                        fetchAvatars(pubKeys);

                        const monthlyData = data.monthly_subscriptions_revenue_usd;

                        const getTotalDaysInMonth = (year, month) => new Date(year, month, 0).getDate();
                        const currentDate = new Date();
                        const currentMonth = currentDate.getUTCMonth() + 1;
                        const currentYear = currentDate.getUTCFullYear();
                        const totalDaysInCurrentMonth = getTotalDaysInMonth(currentYear, currentMonth);
                        const previousMonth = currentMonth === 1 ? 12 : currentMonth - 1;

                       const accumulateTo = (target, arr) => {
                        arr.forEach(([ts, rev]) => {
                            const key = new Date(parseInt(ts, 10) * 1000).toISOString().slice(0, 7); // "YYYY-MM"
                            target[key] = (target[key] || 0) + parseFloat(rev);
                        });
                        };

                        // 1) Total revenue by month (regular + legend + pro)  <-- this is what your code still uses later
                        const revenueByMonth = {};
                        accumulateTo(revenueByMonth, monthlyData.regular);
                        accumulateTo(revenueByMonth, monthlyData.legend);
                        accumulateTo(revenueByMonth, monthlyData.pro);

                        // 2) Separate bucket maps to match the chart groupings
                        const legendProByMonth = {};
                        const regularByMonth = {};
                        accumulateTo(legendProByMonth, monthlyData.legend);
                        accumulateTo(legendProByMonth, monthlyData.pro);
                        accumulateTo(regularByMonth, monthlyData.regular);

                        // Average monthly revenue (same as before)
                        // ---- Averages (use month buckets so it matches the chart) ----
                        const computeAvgFromBuckets = (bucketObj) => {
                        const vals = Object.values(bucketObj || {});
                        if (!vals.length) return '$0';
                        const mean = vals.reduce((a, b) => a + b, 0) / vals.length;
                        return `$${Math.round(mean).toLocaleString('en-US')}`;
                        };

                        // Total avg over all months
                        setMonthlyRevenueAverage(computeAvgFromBuckets(revenueByMonth));

                        // Legend+Pro avg over all months
                        setLegendMonthlyAverage(computeAvgFromBuckets(legendProByMonth));

                        // Regular (Premium) avg over all months
                        setPremiumMonthlyAverage(computeAvgFromBuckets(regularByMonth));

                        const now = new Date();
                        const currentMonthKey = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), 1))
                        .toISOString()
                        .slice(0, 7); // "YYYY-MM"
                        const prevMonthDate = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth() - 1, 1));
                        const previousMonthKey = prevMonthDate.toISOString().slice(0, 7);

                        // Use the pre-aggregated month buckets (same approach as the chart)
                        const currentMonthTotal = revenueByMonth[currentMonthKey] || 0;
                        const previousMonthTotal = revenueByMonth[previousMonthKey] || 0;

                        setCurrentMonthRevenue(`$${Math.round(currentMonthTotal).toLocaleString('en-US')}`);
                        setPreviousMonthRevenue(`$${Math.round(previousMonthTotal).toLocaleString('en-US')}`);

                        // (Optional) keep projection but base it on the same bucketed current total
                        if (currentMonthTotal > 0) {
                        const totalDaysInCurrentMonth = new Date(now.getUTCFullYear(), now.getUTCMonth() + 1, 0).getDate();
                        const projectedRevenue = (currentMonthTotal / now.getUTCDate()) * totalDaysInCurrentMonth;
                        setProjectedMonthRevenue(`$${Math.round(projectedRevenue).toLocaleString('en-US')}`);
                        } else {
                        setProjectedMonthRevenue('N/A');
                        }

                        // --- Legend Revenue (legend + pro) ---
                        // NEW Legend (bucketed like the graph)
                        const currentLegendTotal = legendProByMonth[currentMonthKey] || 0;
                        const previousLegendTotal = legendProByMonth[previousMonthKey] || 0;

                        setCurrentMonthLegendRevenue(`$${Math.round(currentLegendTotal).toLocaleString('en-US')}`);
                        setPreviousMonthLegendRevenue(`$${Math.round(previousLegendTotal).toLocaleString('en-US')}`);

                        if (currentLegendTotal > 0) {
                        const projectedLegend = (currentLegendTotal / currentDate.getUTCDate()) * totalDaysInCurrentMonth;
                        setLegendRevenueProjection(`$${Math.round(projectedLegend).toLocaleString('en-US')}`);
                        } else {
                        setLegendRevenueProjection('N/A');
                        }


                       // NEW Premium (bucketed like the graph)
                        const currentPremiumTotal = regularByMonth[currentMonthKey] || 0;
                        const previousPremiumTotal = regularByMonth[previousMonthKey] || 0;

                        setCurrentMonthPremiumRevenue(`$${Math.round(currentPremiumTotal).toLocaleString('en-US')}`);
                        setPreviousMonthPremiumRevenue(`$${Math.round(previousPremiumTotal).toLocaleString('en-US')}`);

                        if (currentPremiumTotal > 0) {
                        const projectedPremium = (currentPremiumTotal / currentDate.getUTCDate()) * totalDaysInCurrentMonth;
                        setPremiumRevenueProjection(`$${Math.round(projectedPremium).toLocaleString('en-US')}`);
                        } else {
                        setPremiumRevenueProjection('N/A');
                        }
                    }
                setLoading(false);
            } catch (error) {
                console.error('Error fetching API data:', error);
                setLoading(false);
            }
        };
    
        useEffect(() => {
            fetchSubscriptionData();
        }, []);
        
        

    // Helper function to format timestamp
    const formatDate = (timestamp) => {
        const date = new Date(timestamp * 1000);
        return date.toLocaleString('en-US', {
            weekday: 'long',
            year: 'numeric',
            month: 'short',
            day: 'numeric',
            hour: 'numeric',
            minute: 'numeric',
            hour12: true,
        });
    };

    // Helper function to get SKU label based on months
    const getSkuLabel = (months) => {
        switch (months) {
            case 1:
                return "1M";
            case 3:
                return "3M";
            case 12:
                return "12M";
            case -1:
                return "LGD";
            default:
                return months;
        }
    };

    const refreshContent = async () => {
        // Reset all state variables
        setSubscriptionData(null);
        setTableData([]);
        setMonthlyRevenueAverage(0);
        setPreviousMonthRevenue(0);
        setCurrentMonthRevenue(0);
        setProjectedMonthRevenue(0);
        setLegendMonthlyAverage(0);
        setPreviousMonthLegendRevenue(0);
        setCurrentMonthLegendRevenue(0);
        setLegendRevenueProjection(0);
        setPremiumMonthlyAverage(0);
        setPreviousMonthPremiumRevenue(0);
        setCurrentMonthPremiumRevenue(0);
        setPremiumRevenueProjection(0);
        setAvatars({});
        setAvatarsLoading(true);
        setLoading(true);
    
        // Fetch the subscription data again
        await fetchSubscriptionData();
    };

    const renderValue = (value) => `$${Math.round(value).toLocaleString('en-US')}`;

    return (
        <>
            <div className="MainBody">
                <div className="PremiumHeader">
                    <div className="UMTitle">subscriptions</div>
                    <div className="PremiumPageButtons">
                        <button className="RefreshButtons" onClick={refreshContent}>
                            <img src={refresh} alt="RefreshButton"/>
                        </button>
                        <button className="LegendsButton" onClick={navigatetoLegends}>
                            <img src={LegendsButton} alt="LegendsButton"/>
                        </button>
                        <button className="ExpiredButton" onClick={navigateToExpiredSubs}>
                            <img src={expiredButton} alt="ExpiredButton"/>
                        </button>
                        <button className="ChangeNameButton" onClick={navigateToChangeName}>
                            <img src={ChangeName} alt="Change Name Button" />
                        </button>
                        <button className="ReservedNamesButton" onClick={navigateToReservedNames}>
                            <img src={ReservedNamesIcon} alt="Reserved Names" />
                        </button>
                        <button className="PromoCodesButton" onClick={navigateToPromoCodes}>
                            <img src={PromoCodesIcon} alt="Promo Codes" />
                        </button>
                    </div>
                </div>

                <div className="PremiumCardZone">
                    <div className="PremiumCardsContainer">
                        <div className="PremiumCard">
                            <div className="PremiumCardTitle">Active Subs</div>
                            <div className="PremiumCardValue">{subscriptionData?.active_subscriptions ?? 0}</div>
                        </div>
                        <div className="PremiumCard">
                            <div className="PremiumCardTitle">Active Pros</div>
                            <div className="PremiumCardValue">{subscriptionData?.active_pro_subscriptions ?? 0}</div>
                        </div>
                        <div className="PremiumCard">
                            <div className="PremiumCardTitle">New Subs 24h</div>
                            <div className="PremiumCardValue">{subscriptionData?.new_subscriptions_24h ?? 0}</div>
                        </div>
                        <div className="PremiumCard">
                            <div className="PremiumCardTitle">Revenue</div>
                            <div className="PremiumCardValue">{renderValue(subscriptionData?.subscriptions_revenue_usd ?? 0)}</div>
                        </div>
                        <div className="PremiumCard">
                            <div className="PremiumCardTitle">Sub Annual Value</div>
                            <div className="PremiumCardValue">
                                {renderValue(
                                    calculateSubAnnualValue(
                                        subscriptionData?.active_subscriptions ?? 0,
                                        subscriptionData?.active_pro_subscriptions ?? 0
                                    )
                                )}
                            </div>
                        </div>
                    </div>
                </div>

                <div className="MonthlyRevenueZone">
                    <div className="MonthlyRevenues">
                        <div className="MonthlyRevenuesTitle">Monthly Revenues</div>
                        <div className="MonthlyRevenueGraph"><MonthlyRevenuesGraph /></div>
                    </div>
                    <div className="MontlyRevenueTitles">
                        <div className="MonthlyRevenueTitle">Avg. Monthly Revenue</div>
                        <div className="MonthlyRevenueTitle">Previous Month Revenue</div>
                        <div className="MonthlyRevenueTitle">Current Month Revenue</div>
                        <div className="MonthlyRevenueTitle">Current Month Revenue Projection</div>
                    </div>
                    <div className="TotalMonthlyRevenueList">
                        <div className="TotalMonthlyRevenue">{monthlyRevenueAverage}</div>
                        <div className="TotalMonthlyRevenue">{previousMonthRevenue}</div>
                        <div className="TotalMonthlyRevenue">{currentMonthRevenue}</div>
                        <div className="TotalMonthlyRevenue">{projectedMonthRevenue}</div>
                    </div>
                    <div className="LegendMonthlyRevenueList">
                        <div className="LegendMonthlyRevenue">{legendMonthlyAverage}</div>
                        <div className="LegendMonthlyRevenue">{previousMonthLegendRevenue}</div>
                        <div className="LegendMonthlyRevenue">{currentMonthLegendRevenue}</div>
                        <div className="LegendMonthlyRevenue">{legendRevenueProjection}</div>
                    </div>
                    <div className="PremiumMonthlyRevenueList">
                        <div className="PremiumMonthlyRevenue">{premiumMonthlyAverage}</div>
                        <div className="PremiumMonthlyRevenue">{previousMonthPremiumRevenue}</div>
                        <div className="PremiumMonthlyRevenue">{currentMonthPremiumRevenue}</div>
                        <div className="PremiumMonthlyRevenue">{premiumRevenueProjection}</div>
                    </div>
                </div>


                <div className="SubscriptionList">
    {tableData.map((item, index) => {
  const short = (item.short_product_id || "").toUpperCase();
  const pid = (item.product_id || "").toUpperCase();

  const isLegend = pid.includes("LEGEND") || short === "LGD" || short.includes("LGD");
  const isPro = short.includes("PRO") || pid.includes("PRO");
  const isTeam = short.includes("TEAM") || pid.includes("TEAM"); // <= TEAM handled here

  const rowClass = isLegend
    ? "lgd-row"
    : (isPro || isTeam)   // TEAM shares the gold (pro) style
    ? "pro-row"
    : "";

  const skuLabel = item.short_product_id ?? getSkuLabel(item.months);
  const userName = item.name || 'Unknown';
        const avatarUrl = avatars[item.receiver_pubkey]?.avatar || DefaultAvatar; // Fetch avatar or use default

        return (
            <div key={index} className={`SubscriptionRow ${rowClass}`}>
                <div className="Avatar">
    <a
        href={`https://primal.net/${item.name || 'Unknown'}`}
        target="_blank"
        rel="noopener noreferrer"
    >
        <img
            src={avatars[item.receiver_pubkey] || DefaultAvatar} // Use the mapped avatar or default
            alt="Avatar"
            className="avatar-img"
        />
    </a>
</div>

                <div className="RowItem NameRowItem">
                    <a
                        href={`https://primal.net/${userName}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="NameLink"
                    >
                        {userName}
                    </a>
                </div>
                <div className="RowItem">{skuLabel}</div>
                <div className="RowItem">{item.revenue_usd != null ? `$${parseFloat(item.revenue_usd).toFixed(2)}` : 'N/A'}</div>
                <div className="RowItem">{item.revenue_type ?? 'N/A'}</div>
                <div className="RowItem">{item.origin ?? 'N/A'}</div>
                <div className="RowItem DateRowItem">{formatDate(item.purchased_at)}</div>
            </div>
        );
    })}
</div>
            </div>
        </>
    );
};

export default PremiumSubs;
